// AIOE owns it; the workbench copies it (D426).
/**
 * A person's own apps and Home preferences, private to them in each organisation (D426): so what they build and how they
 * arrange Home follows them between workbenches. Nobody else reads them, an administrator included; there is no admin route.
 *
 *   GET    /me/v1/apps?since=<rev>          -> MyAppsList      changes since a revision (none given: every live app, full)
 *   PUT    /me/v1/apps/:id                  MyAppPut -> MyAppSaved     (201 new, 200 otherwise)
 *   DELETE /me/v1/apps/:id                  MyAppDelete? -> { rev }
 *   GET    /me/v1/preferences/home          -> HomePrefsRecord  (homePrefs.ts)
 *   PUT    /me/v1/preferences/home          HomePrefsPut -> HomePrefsSaved
 *
 * Hints: `me:apps` and `me:home` (resourceChanges.ts) go only to the person's own machines.
 */
import { z } from "zod";
export const MY_APPS_PATH = "/me/v1/apps";
export const MY_HOME_PREFS_PATH = "/me/v1/preferences/home";
/** The most live apps a person keeps in one organisation. */
export const MY_APPS_LIMIT = 200;
/** A saved app's manifest, serialised, at most. */
export const MY_APP_MANIFEST_MAX_BYTES = 512 * 1024;
/** How long a deleted app is remembered so a machine that was away still hears it went. */
export const MY_APP_TOMBSTONE_DAYS = 30;
const hash = z.string().regex(/^[0-9a-f]{64}$/);
/** `PUT /me/v1/apps/:id`. The manifest is checked with `microappManifestSchema`; a `hash` sent is ignored (the home computes it). */
export const myAppPutSchema = z.object({ manifest: z.unknown(), baseHash: hash.optional(), hash: z.unknown().optional() });
export const myAppSavedSchema = z.object({ rev: z.number().int().positive(), hash, updatedAt: z.string() });
/** `DELETE /me/v1/apps/:id`, body optional. */
export const myAppDeleteSchema = z.object({ baseHash: hash.optional() });
export const myAppRowSchema = z.object({
    id: z.string(),
    manifest: z.unknown(),
    hash,
    updatedAt: z.string(),
    deleted: z.literal(true).optional(),
    /** Why the organisation's `microapps` clause refuses this app now, in words. The app is still returned. */
    blocked: z.string().optional(),
});
export const myAppsListSchema = z.object({ rev: z.number().int().nonnegative(), full: z.boolean(), apps: z.array(myAppRowSchema) });
/** 409 `app_changed`: the stored app is not the one the caller built on (`current` null: there is none). */
export const myAppChangedSchema = z.object({ error: z.literal("app_changed"), current: z.object({ id: z.string(), manifest: z.unknown(), hash, updatedAt: z.string() }).nullable() });
/** 403 `refused_by_policy`. */
export const myAppRefusedSchema = z.object({ error: z.literal("refused_by_policy"), reason: z.string() });
/** 409 `app_limit`. */
export const myAppLimitSchema = z.object({ error: z.literal("app_limit"), limit: z.number().int() });
/** `PUT /me/v1/preferences/home` answer. */
export const homePrefsSavedSchema = z.object({ rev: z.number().int().positive(), updatedAt: z.string() });
/** 409 `prefs_changed`. */
export const homePrefsChangedSchema = z.object({ error: z.literal("prefs_changed"), current: z.object({ rev: z.number().int().nonnegative(), prefs: z.unknown().nullable(), updatedAt: z.string().nullable() }) });
//# sourceMappingURL=myApps.js.map